import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import Spinner from "../../components/ui/spinner";
import { FileCode } from "lucide-react";
import { useCreatePrototypeMutation } from "../../data/mutations/prototypes";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function UploadView({
    adminPassword,
    onBack,
    onSuccess,
}: {
    adminPassword: string;
    onBack: () => void;
    onSuccess: (slug: string, password: string) => void;
}) {
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState("");
    const createPrototype = useCreatePrototypeMutation();

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const f = acceptedFiles[0];
        if (!f) return;
        if (f.size > MAX_FILE_SIZE) {
            setError("File too large. Maximum size is 5 MB.");
            return;
        }
        setError("");
        setFile(f);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "text/html": [".html"] },
        maxFiles: 1,
    });

    const handlePublish = async () => {
        if (!file || !name || !password) return;
        setError("");
        try {
            const result = await createPrototype.mutateAsync({ adminPassword, name, password, htmlFile: file });
            onSuccess(result.slug, password);
        } catch {
            setError("Failed to publish. Please try again.");
        }
    };

    return (
        <div className="space-y-6">
            <Button variant="ghost" onClick={onBack}>
                &larr; Back to dashboard
            </Button>

            <div>
                <h1 className="text-2xl font-bold">Publish a prototype</h1>
                <p className="text-muted-foreground">Upload an HTML file and configure how it should be shared.</p>
            </div>

            <div className="space-y-6">
                <div>
                    <Label>HTML file</Label>
                    <div
                        {...getRootProps()}
                        className={`mt-2 cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                            isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25"
                        } ${file ? "border-solid border-muted bg-muted/50" : ""}`}
                    >
                        <input {...getInputProps()} />
                        {file ? (
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <FileCode className="h-5 w-5 text-primary" />
                                    <span className="font-medium">{file.name}</span>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        setFile(null);
                                    }}
                                >
                                    Remove
                                </Button>
                            </div>
                        ) : (
                            <>
                                <p className="font-medium">Drop your .html file here or click to browse</p>
                                <p className="text-sm text-muted-foreground">Single self-contained HTML file</p>
                            </>
                        )}
                    </div>
                </div>

                <div>
                    <Label htmlFor="name">Prototype name</Label>
                    <Input
                        id="name"
                        value={name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                        placeholder="e.g. Onboarding Flow v2"
                        className="mt-2"
                    />
                </div>

                <div>
                    <Label htmlFor="viewer-password">Viewer password</Label>
                    <Input
                        id="viewer-password"
                        type="text"
                        value={password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                        placeholder="Set a password for viewers"
                        className="mt-2"
                    />
                    <p className="mt-1 text-sm text-muted-foreground">
                        Share this password with people who need to view the prototype.
                    </p>
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <Button
                    onClick={handlePublish}
                    disabled={!name || !password || !file || createPrototype.isPending}
                    className="w-full"
                    size="lg"
                >
                    {createPrototype.isPending ? (
                        <>
                            <Spinner className="mr-2 h-4 w-4" /> Publishing...
                        </>
                    ) : (
                        "Publish prototype"
                    )}
                </Button>
            </div>
        </div>
    );
}
